/**
 * End-of-speech detection for the live ears, by energy: speech is a level well above a noise floor that follows the
 * room, a turn begins after ~150 ms of it and ends after ~700 ms of quiet. push(level, dtMs) takes one frame's RMS
 * (0..1) and answers 'start', 'end' or null. Pure, so it is tested without audio.
 */
export function createEndpointer({ endSilenceMs = 700, minSpeechMs = 150, floorTimeMs = 300 } = {}) {
  let floor = null;
  let speaking = false;
  let loudMs = 0;
  let quietMs = 0;
  let clock = 0;
  let floorMin = 0;
  let floorMinUntil = 0;

  const reset = () => { speaking = false; loudMs = 0; quietMs = 0; };

  function push(level, dtMs) {
    clock += dtMs;
    if (floor == null) floor = Math.min(level, 0.02); // a first frame that is already loud is speech, not the room
    const threshold = floor * 2.5 + 0.015;
    const loud = level > threshold;
    if (!speaking) {
      if (loud) {
        loudMs += dtMs;
        if (loudMs >= minSpeechMs) { speaking = true; quietMs = 0; return 'start'; }
        return null;
      }
      loudMs = 0;
      // The floor follows the room while nobody speaks: quick to rise to a new hum, quicker to fall.
      floor += (level - floor) * (1 - Math.exp(-dtMs / floorTimeMs));
      if (clock < floorMinUntil && floor < floorMin) floor = floorMin; // noise that was learned stays learned for a while
      return null;
    }
    if (loud) { quietMs = 0; return null; }
    quietMs += dtMs;
    if (quietMs >= endSilenceMs) { speaking = false; loudMs = 0; quietMs = 0; return 'end'; }
    return null;
  }

  /** The energy just heard turned out to be noise (no words in it): its loudest level is the room now, for 30 s. */
  function learnNoise(peak) { floorMin = Math.max(floorMin, peak / 2.5); floorMinUntil = clock + 30_000; floor = Math.max(floor ?? 0, floorMin); reset(); }

  return { push, reset, learnNoise, get speaking() { return speaking; }, get floor() { return floor; }, get quietMs() { return speaking ? quietMs : 0; } };
}
